import { Fragment, useCallback, useEffect, useState } from "react";
import { Button, Checkbox, Flex, Icon, Modal } from "@repo/ping-design";

import "./TopBuildingsListConfigureModal.scss";

export type ColumnConfigItem = {
  key: string;
  name: string;
  isVisible: boolean;
  // Nested groups, outermost first; see ConfigurableColumn.groups.
  groups?: string[];
};

const isInGroup = (item: ColumnConfigItem, path: string[]) =>
  path.every((name, depth) => item.groups?.[depth] === name);

// Group header rows that start before `index`: one per level where its path leaves the previous item's.
const groupHeadersBefore = (items: ColumnConfigItem[], index: number): string[][] => {
  const path = items[index].groups ?? [];
  const previous = index > 0 ? (items[index - 1].groups ?? []) : [];
  const shared = path.findIndex((name, depth) => previous[depth] !== name);
  if (shared === -1) return [];
  return path.slice(shared).map((_, offset) => path.slice(0, shared + offset + 1));
};

type TopBuildingsListConfigureModalProps = {
  isOpen: boolean;
  onClose: () => void;
  items: ColumnConfigItem[];
  isCustomized: boolean;
  onSave: (items: ColumnConfigItem[]) => void;
  onReset: () => void;
};

export const TopBuildingsListConfigureModal = ({
  isOpen,
  onClose,
  items,
  isCustomized,
  onSave,
  onReset,
}: TopBuildingsListConfigureModalProps) => {
  const [localItems, setLocalItems] = useState<ColumnConfigItem[]>(items);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const handleToggle = useCallback((key: string) => {
    setLocalItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, isVisible: !item.isVisible } : item,
      ),
    );
  }, []);

  // Shows the whole group unless all of it is already visible, in which case it hides it.
  const handleToggleGroup = useCallback((path: string[]) => {
    setLocalItems((prev) => {
      const show = prev.some((item) => isInGroup(item, path) && !item.isVisible);
      return prev.map((item) => (isInGroup(item, path) ? { ...item, isVisible: show } : item));
    });
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) return;
      setLocalItems((prev) => {
        const next = [...prev];
        const [item] = next.splice(draggedIndex, 1);
        next.splice(index, 0, item);
        return next;
      });
      setDraggedIndex(index);
    },
    [draggedIndex],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const handleSave = useCallback(() => {
    onSave(localItems);
  }, [onSave, localItems]);

  const handleCancel = useCallback(() => {
    setTimeout(() => setLocalItems(items), 0);
    onClose();
  }, [onClose, items]);

  return (
    <Modal
      title="Configure Columns"
      isOpen={isOpen}
      onClose={handleCancel}
      className="TopBuildingsListConfigureModal"
      renderContent={() => (
        <div className="TopBuildingsListConfigureModal__Content">
          <div className="TopBuildingsListConfigureModal__Content__Header">
            <div>Column Name</div>
            <div>Visibility</div>
          </div>
          {localItems.map((item, index) => (
            <Fragment key={item.key}>
              {groupHeadersBefore(localItems, index).map((path) => {
                const members = localItems.filter((member) => isInGroup(member, path));
                const visible = members.filter((member) => member.isVisible).length;
                return (
                  <div
                    key={path.join("/")}
                    className="TopBuildingsListConfigureModal__GroupItem"
                    style={{ paddingLeft: `${(path.length - 1) * 1.5}rem` }}
                  >
                    <div className="TopBuildingsListConfigureModal__GroupItem__Label">
                      {path[path.length - 1]}
                    </div>
                    <Checkbox
                      name={`group:${path.join("/")}`}
                      checked={visible === members.length}
                      indeterminate={visible > 0 && visible < members.length}
                      onChange={() => handleToggleGroup(path)}
                    />
                  </div>
                );
              })}
              <div
                className={`TopBuildingsListConfigureModal__ColumnItem${
                  draggedIndex === index
                    ? " TopBuildingsListConfigureModal__ColumnItem--Dragging"
                    : ""
                }`}
                style={item.groups?.length ? { paddingLeft: `${item.groups.length * 1.5}rem` } : undefined}
                onDragOver={(e) => handleDragOver(e, index)}
              >
                <div
                  className="TopBuildingsListConfigureModal__ColumnItem__Label"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="TopBuildingsListConfigureModal__ColumnItem__Label__IconContainer">
                    <Icon iconName="GripVertical" size={16} />
                  </div>
                  <div className="TopBuildingsListConfigureModal__ColumnItem__Label__LabelText">
                    {item.name}
                  </div>
                </div>
                <Checkbox
                  name={item.key}
                  checked={item.isVisible}
                  onChange={() => handleToggle(item.key)}
                />
              </div>
            </Fragment>
          ))}
        </div>
      )}
      renderFooter={() => (
        <Flex
          gap="2"
          justify="space-between"
          className="TopBuildingsListConfigureModal__Footer"
        >
          <Button
            label="Reset Defaults"
            variant="outline"
            disabled={!isCustomized}
            onClick={onReset}
          />
          <Flex gap="2">
            <Button label="Cancel" variant="ghost" onClick={handleCancel} />
            <Button label="Save" onClick={handleSave} />
          </Flex>
        </Flex>
      )}
    />
  );
};
